/**
 * WifiAccessPointForm — Formulario full-screen para puntos de acceso WiFi.
 * Pestañas: General / Radio / Ubicación / Notas
 * SmartButtons: Vendor, Estándar/Banda, Controlador, PoE, Estado
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  WifiIcon,
  SignalIcon,
  BoltIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Form as BsForm } from 'react-bootstrap';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import WifiApPortsPanel from './WifiApPortsPanel';
import ConnectionDiagram from './ConnectionDiagram';
import FormCardsShell from './FormCardsShell';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import LocationTreeSelect from './common/LocationTreeSelect';
import { CampoSelect, opcionesDeCatalogo } from './common/Sf2Fields';

// ── Constantes ────────────────────────────────────────────────────────────────

const VENDORS = [
  { value: 'ubiquiti',  label: 'Ubiquiti UniFi' },
  { value: 'cisco',     label: 'Cisco (WLC / Catalyst)' },
  { value: 'aruba',     label: 'HPE Aruba' },
  { value: 'ruckus',    label: 'Ruckus / CommScope' },
  { value: 'meraki',    label: 'Cisco Meraki' },
  { value: 'juniper',   label: 'Juniper Mist' },
  { value: 'cambium',   label: 'Cambium Networks' },
  { value: 'extreme',   label: 'Extreme Networks' },
  { value: 'tp_link',   label: 'TP-Link / Omada' },
  { value: 'zyxel',     label: 'Zyxel' },
  { value: 'mikrotik',  label: 'MikroTik' },
  { value: 'fortinet',  label: 'Fortinet FortiAP' },
  { value: 'other',     label: 'Otro' },
];

/* Marca del catálogo de dispositivos que corresponde a cada fabricante del AP.
   El `vendor` es un enum, así que no se puede dar de alta: lo que se da de alta
   son los MODELOS, colgando de esta marca (C13/C14 del estándar de fichas). */
const VENDOR_CATALOG_NAMES = {
  ubiquiti: 'Ubiquiti',
  cisco:    'Cisco',
  aruba:    'HPE Aruba',
  ruckus:   'Ruckus',
  meraki:   'Cisco Meraki',
  juniper:  'Juniper',
  cambium:  'Cambium',
  extreme:  'Extreme',
  tp_link:  'TP-Link',
  zyxel:    'Zyxel',
  mikrotik: 'MikroTik',
  fortinet: 'Fortinet',
};

const WIFI_STANDARDS = [
  { value: '802.11n',       label: '802.11n (Wi-Fi 4)' },
  { value: '802.11ac',      label: '802.11ac (Wi-Fi 5)' },
  { value: '802.11ax',      label: '802.11ax (Wi-Fi 6)' },
  { value: '802.11ax_6ghz', label: '802.11ax / 6 GHz (Wi-Fi 6E)' },
  { value: '802.11be',      label: '802.11be (Wi-Fi 7)' },
  { value: 'mixed',         label: 'Múltiples estándares' },
  { value: 'other',         label: 'Otro' },
];

const BANDS = [
  { value: '2.4ghz',   label: '2.4 GHz' },
  { value: '5ghz',     label: '5 GHz' },
  { value: '6ghz',     label: '6 GHz' },
  { value: 'dual_band', label: 'Dual-band (2.4 + 5 GHz)' },
  { value: 'tri_band',  label: 'Tri-band (2.4 + 5 + 5/6 GHz)' },
  { value: 'quad_band', label: 'Quad-band' },
];

const MOUNT_TYPES = [
  { value: 'ceiling',  label: 'Techo' },
  { value: 'wall',     label: 'Pared' },
  { value: 'desktop',  label: 'Sobremesa' },
  { value: 'outdoor',  label: 'Exterior' },
  { value: 'pole',     label: 'Poste' },
  { value: 'other',    label: 'Otro' },
];

const STATUS_COLORS = {
  active:      { bg: 'rgba(16,185,129,.15)',   color: '#6ee7b7', label: 'Activo' },
  maintenance: { bg: 'rgba(234,179,8,.15)',    color: '#fde68a', label: 'Mantenimiento' },
  decom:       { bg: 'rgba(239,68,68,.15)',    color: '#fca5a5', label: 'Decom' },
  planned:     { bg: 'rgba(59,130,246,.15)',   color: '#93c5fd', label: 'Planificado' },
};

const EMPTY_FORM = {
  name: '', code: '', vendor: 'ubiquiti', model: '',
  serial_number: '', asset_tag: '', firmware_version: '',
  mac_address: '', management_ip: '',
  wifi_standard: '802.11ax', band: 'dual_band',
  tx_power_dbm: '', max_clients: '',
  poe_powered: true,
  mount_type: 'ceiling',
  location_floor: '', location_room: '',
  controller_id: '', location_id: '',
  status: 'active', notes: '',
};

// ── Componente ────────────────────────────────────────────────────────────────

export default function WifiAccessPointForm({ ap, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(ap?.id);

  /* Modo de la ficha: 'view' entra en LECTURA (es lo que abre el clic en la fila
     del listado), 'edit'/'create' editan. Sin `initialMode`, edita como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);

  const [form,        setForm]        = useState(EMPTY_FORM);
  const [controllers, setControllers] = useState([]);
  const [locations,   setLocations]   = useState([]);
  // Catálogo de dispositivos (marca + modelo) para el desplegable y el alta.
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions,        setModelOptions]        = useState([]);
  const [catalogSaving,       setCatalogSaving]       = useState(false);
  const [catalogError,        setCatalogError]        = useState('');
  const [addingModel,         setAddingModel]         = useState(false);
  const [newModel,            setNewModel]            = useState('');
  const [tab,         setTab]         = useState(isCards ? 'resumen' : 'general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving,      setSaving]      = useState(false);
  const [suggesting,  setSuggesting]  = useState(false);
  const [suggestingCode, setSuggestingCode] = useState(false);
  const [isDirty,     setIsDirty]     = useState(false);
  const [apiError,    setApiError]    = useState('');
  const [apSsids,     setApSsids]     = useState([]);        // SSIDs propios (standalone)
  const [ctrlSsids,   setCtrlSsids]   = useState([]);        // SSIDs del controlador (heredados, read-only)
  const [editSsid,    setEditSsid]    = useState(null);
  const [ssidSaving,  setSsidSaving]  = useState(false);
  const [ssidError,   setSsidError]   = useState('');
  const [radiusServers, setRadiusServers] = useState([]);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este punto de acceso',
    actionLabel: 'pasas a otro AP o cierras la ficha',
    save: () => handleSave({ keepEditing: editing }),
  });

  // Clonado del AP (mismo modal que el de los switches)
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName,      setCloneName]      = useState('');
  const [cloning,        setCloning]        = useState(false);
  const [cloneError,     setCloneError]     = useState(null);

  // Bootstrap: cargar listas y formulario
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [ctrlRes, locRes, mfrRes, modRes] = await Promise.all([
          api.getWifiControllers(companyId),
          api.getLocations('', companyId),
          api.getManufacturers(companyId, { deviceType: 'wifi_ap' }).catch(() => ({ data: [] })),
          api.getDeviceModels(companyId, { deviceType: 'wifi_ap' }).catch(() => ({ data: [] })),
        ]);
        setControllers(ctrlRes.data || []);
        setLocations(locRes.data || []);
        setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
        setModelOptions(Array.isArray(modRes?.data) ? modRes.data : []);
      } catch (e) { console.error('Load lists error', e); }
    };
    if (companyId) fetchLists();
  }, [companyId]);

  useEffect(() => {
    if (ap) {
      setForm({
        name:           ap.name || '',
        code:           ap.code || '',
        vendor:         ap.vendor || 'ubiquiti',
        model:          ap.model || '',
        serial_number:  ap.serial_number || '',
        asset_tag:      ap.asset_tag || '',
        firmware_version: ap.firmware_version || '',
        mac_address:    ap.mac_address || '',
        management_ip:  ap.management_ip || '',
        wifi_standard:  ap.wifi_standard || '802.11ax',
        band:           ap.band || 'dual_band',
        tx_power_dbm:   ap.tx_power_dbm ?? '',
        max_clients:    ap.max_clients ?? '',
        poe_powered:    ap.poe_powered !== false,
        mount_type:     ap.mount_type || 'ceiling',
        location_floor: ap.location_floor || '',
        location_room:  ap.location_room || '',
        controller_id:  ap.controller_id ?? '',
        location_id:    ap.location_id ?? '',
        ssids:          ap.ssids || [],
        status:         ap.status || 'active',
        notes:          ap.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false);
  }, [ap]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };
  const handleSuggest = async () => {
    try {
      setSuggesting(true);
      const res = await api.suggestName({ type: 'wifi_ap', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) sf('name', suggested);
    } catch {} finally { setSuggesting(false); }
  };
  /** ✨ del campo Código: reserva el número de nomenclatura (WAP-0001). */
  const handleSuggestCode = async () => {
    try {
      setSuggestingCode(true);
      const res = await api.suggestName({ type: 'wifi_ap', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) sf('code', suggested);
    } catch {} finally { setSuggestingCode(false); }
  };
  /* El modelo pertenece al fabricante: al cambiarlo se limpia (C14). */
  const handleVendorChange = (value) => {
    setForm(f => ({ ...f, vendor: value, model: '' }));
    setIsDirty(true);
    setAddingModel(false);
    setNewModel('');
    setCatalogError('');
  };
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    sf(name, type === 'checkbox' ? checked : value);
  };

  // ── Catálogo de dispositivos: modelos del fabricante ──────────────────────
  const marcaCatalogo = VENDOR_CATALOG_NAMES[form.vendor] || '';
  const opcionesModelo = useMemo(() => {
    const marca = marcaCatalogo.toLowerCase();
    const mfr = marca
      ? manufacturerOptions.find(m => {
          const n = (m.name || '').toLowerCase();
          return n === marca || n.startsWith(marca) || marca.startsWith(n);
        })
      : null;
    const vistos = new Set();
    const items = (modelOptions || []).filter(m => {
      if (!m?.name) return false;
      // Modelos de otra marca fuera; los globales (sin marca) se quedan.
      if (mfr && m.manufacturer_id && m.manufacturer_id !== mfr.id) return false;
      const key = m.name.toLowerCase();
      if (vistos.has(key)) return false;
      vistos.add(key);
      return true;
    });
    // El modelo guardado que ya no esté en el catálogo no puede desaparecer.
    const guardado = (form.model || '').trim();
    if (guardado && !items.some(m => (m.name || '').toLowerCase() === guardado.toLowerCase())) {
      items.unshift({ name: guardado });
    }
    return opcionesDeCatalogo(items, '— Sin modelo —');
  }, [modelOptions, manufacturerOptions, marcaCatalogo, form.model]);

  /** Alta del modelo en el maestro sin salir de la ficha (C13). */
  const saveModelToCatalog = async (nombre) => {
    const modelo = (nombre || '').trim();
    if (!modelo || !companyId) return;
    setCatalogSaving(true);
    setCatalogError('');
    try {
      const marca = marcaCatalogo;
      const mfr = marca
        ? manufacturerOptions.find(m => (m.name || '').toLowerCase() === marca.toLowerCase())
        : null;
      await api.createDeviceModel(
        {
          name: modelo,
          device_type: 'wifi_ap',
          manufacturer_id: mfr?.id,
          manufacturer_name: mfr ? undefined : marca || undefined,
        },
        companyId
      );
      const [modRes, mfrRes] = await Promise.all([
        api.getDeviceModels(companyId, { deviceType: 'wifi_ap' }).catch(() => ({ data: [] })),
        api.getManufacturers(companyId, { deviceType: 'wifi_ap' }).catch(() => ({ data: [] })),
      ]);
      setModelOptions(Array.isArray(modRes?.data) ? modRes.data : []);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      sf('model', modelo);
    } catch (e) {
      setCatalogError(e.message || 'No se pudo dar de alta el modelo');
    } finally {
      setCatalogSaving(false);
    }
  };

  // Cargar SSIDs del controlador cuando cambia controller_id
  useEffect(() => {
    if (!form.controller_id || !companyId) { setCtrlSsids([]); return; }
    const ctrl = controllers.find(c => String(c.id) === String(form.controller_id));
    // Si ya tenemos los ssids en el objeto controlador (viene del to_dict), los usamos directamente
    if (ctrl?.ssids) { setCtrlSsids(ctrl.ssids); return; }
    // Si no, los pedimos a la API
    api.getWifiSSIDs(companyId, { controller_id: form.controller_id })
      .then(r => setCtrlSsids(r.data || []))
      .catch(() => setCtrlSsids([]));
  }, [form.controller_id, controllers, companyId]);

  // === SSIDs propios (standalone) ==========================================
  // Cargar servidores RADIUS
  useEffect(() => {
    if (!companyId) return;
    api.getWifiRadiusServers(companyId).then(r => setRadiusServers(r.data || [])).catch(() => {});
  }, [companyId]);

  const handleSsidSave = async (ssidData) => {
    if (!ssidData.name.trim()) { setSsidError('El nombre del SSID es obligatorio'); return; }
    setSsidSaving(true); setSsidError('');
    try {
      const payload = {
        ...ssidData,
        access_point_id: ap.id,
        vlan_id: ssidData.vlan_id !== '' ? Number(ssidData.vlan_id) : null,
        radius_port: Number(ssidData.radius_port) || 1812,
      };
      if (ssidData.id) {
        const res = await api.updateWifiSSID(ssidData.id, payload, companyId);
        setApSsids(prev => prev.map(s => s.id === ssidData.id ? res.data : s));
      } else {
        const res = await api.createWifiSSID(payload, companyId);
        setApSsids(prev => [...prev, res.data]);
      }
      setEditSsid(null);
    } catch (e) { setSsidError(e.message); }
    finally { setSsidSaving(false); }
  };

  const handleSsidDelete = async (ssidId) => {
    if (!window.confirm('¿Eliminar este SSID?')) return;
    try {
      await api.deleteWifiSSID(ssidId, companyId);
      setApSsids(prev => prev.filter(s => s.id !== ssidId));
      if (editSsid?.id === ssidId) setEditSsid(null);
    } catch (e) { setApiError(e.message); }
  };

  // === Guardar ==============================================================
  const handleSave = async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) { setApiError('El nombre es obligatorio'); return false; }
    setSaving(true); setApiError('');
    try {
      const payload = {
        ...form,
        tx_power_dbm:  form.tx_power_dbm  !== '' ? Number(form.tx_power_dbm) : null,
        max_clients:   form.max_clients   !== '' ? Number(form.max_clients) : null,
        controller_id: form.controller_id !== '' ? Number(form.controller_id) : null,
        location_id:   form.location_id   !== '' ? Number(form.location_id) : null,
      };
      let creado = null;
      if (isEditing) await api.updateWifiAP(ap.id, payload, companyId);
      else           creado = registroGuardado(await api.createWifiAP(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      if (isCards && !keepEditing) setEditing(false);
      return true;
    } catch (e) { setApiError(e.message); return false; }
    finally { setSaving(false); }
  };

  /* El borrado vive en el listado (con el diálogo único del CMDB): aquí no hay
     botón de borrar ni `window.confirm`. */

  // ── Clonado ────────────────────────────────────────────────────────────────
  const openCloneModal = () => {
    setCloneName(`${form.name || ap?.name || ''} (copia)`);
    setCloneError(null);
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    if (!cloneName.trim() || cloning) return;
    try {
      setCloning(true); setCloneError(null);
      await api.cloneWifiAP(ap.id, cloneName.trim(), companyId);
      setShowCloneModal(false);
      onSave();
    } catch (e) { setCloneError(e.message); }
    finally { setCloning(false); }
  };

  const handleDiscard = () => {
    if (ap) {
      setForm({
        name:           ap.name || '',
        code:           ap.code || '',
        vendor:         ap.vendor || 'ubiquiti',
        model:          ap.model || '',
        serial_number:  ap.serial_number || '',
        asset_tag:      ap.asset_tag || '',
        firmware_version: ap.firmware_version || '',
        mac_address:    ap.mac_address || '',
        management_ip:  ap.management_ip || '',
        wifi_standard:  ap.wifi_standard || '802.11ax',
        band:           ap.band || 'dual_band',
        tx_power_dbm:   ap.tx_power_dbm ?? '',
        max_clients:    ap.max_clients ?? '',
        poe_powered:    ap.poe_powered !== false,
        mount_type:     ap.mount_type || 'ceiling',
        location_floor: ap.location_floor || '',
        location_room:  ap.location_room || '',
        controller_id:  ap.controller_id ?? '',
        location_id:    ap.location_id ?? '',
        status:         ap.status || 'active',
        notes:          ap.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false); setApiError('');
  };

  // Datos derivados
  const statusInfo  = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const bandLabel   = BANDS.find(b => b.value === form.band)?.label || form.band;
  const stdLabel    = WIFI_STANDARDS.find(s => s.value === form.wifi_standard)?.label || form.wifi_standard;
  const ctrlName    = controllers.find(c => String(c.id) === String(form.controller_id))?.name || null;
  const isStandalone = !form.controller_id;
  const ssidCount   = isStandalone ? apSsids.length : ctrlSsids.length;

  const STATUS_CYCLE = ['active', 'maintenance', 'planned', 'decom'];

  // ── Datos de la variante tarjetas ────────────────────────────────────────
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = form.location_id ? locationPath(mapaUbicaciones, form.location_id) : '';
  const ESTADO_COLOR = { active: '#16a34a', maintenance: '#f59e0b', planned: '#0ea5e9', decom: '#dc2626' };
  const estadoColor = ESTADO_COLOR[form.status] || '#94a3b8';
  const mountLabel = MOUNT_TYPES.find(m => m.value === form.mount_type)?.label || form.mount_type || '—';

  /* En tarjetas el panel se marca `sf2-pane` para que sus secciones se pinten
     como tarjeta; en la clásica se queda en `dv-tab-pane` (no cambia nada). */
  const paneClass = isCards ? 'dv-tab-pane sf2-pane' : 'dv-tab-pane';

  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => { handleDiscard(); setEditing(false); };

  /* Pasar de ficha a ficha sin salir al listado (§12.A5). */
  const irAAP = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      onNavigate?.(destino);
    });
  };

  // Pestañas: en tarjetas la primera es General y General/Ubicación NO existen
  // (esa información se edita en el propio General, como en la ficha de servidor).
  const tabsDef = isCards
    ? [
        { key: 'resumen', label: 'General' },
        { key: 'radio',   label: `Radio / SSIDs (${ssidCount})` },
        ...(isEditing ? [{ key: 'ipam',   label: 'IPAM' }] : []),
        ...(isEditing ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
        ...(isEditing ? [{ key: 'cable',  label: 'Conexión' }] : []),
      ]
    : [
        { key: 'general',   label: 'General' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'radio',     label: 'Radio / SSIDs' },
        ...(isEditing ? [{ key: 'ipam',   label: 'IPAM' }] : []),
        ...(isEditing ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
        ...(isEditing ? [{ key: 'cable',  label: 'Conexión' }] : []),
      ];

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Puntos de Acceso
        </button>
        <DetailCrumb label={form.name} />

        {/* ── Navegación entre fichas: a la izquierda del spacer ── */}
        <FormPager
          items={navItems}
          index={navIndex}
          label="Punto de acceso"
          disabled={saving}
          onChange={irAAP}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300 }}>
            ⚠ {apiError}
          </span>
        )}
        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              {!editing && (
                <button
                  className="dv-btn dv-btn-primary"
                  type="button"
                  onClick={() => setEditing(true)}
                  title="Editar la ficha"
                >
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              {isEditing && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={openCloneModal}
                  title="Clonar este punto de acceso con toda su configuración"
                >
                  <DocumentDuplicateIcon style={{ width: 14, height: 14 }} /> Copiar
                </button>
              )}
            </>
          ) : (
          <>
          <span
            className="dv-status-badge"
            style={{ background: statusInfo.bg, color: statusInfo.color, cursor: editing ? 'pointer' : 'default' }}
            title={editing ? 'Clic para cambiar estado' : statusInfo.label}
            onClick={() => {
              if (!editing) return;
              const idx = STATUS_CYCLE.indexOf(form.status);
              sf('status', STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]);
            }}
          >
            ● {statusInfo.label}
          </span>
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon style={{ width: 14, height: 14 }} /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              {isEditing && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={openCloneModal}
                  title="Clonar este punto de acceso con toda su configuración"
                >
                  <DocumentDuplicateIcon style={{ width: 14, height: 14 }} /> Copiar
                </button>
              )}
            </>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── Modal de clonado ── */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <span style={{ marginRight: 8 }}>📋</span>
            Clonar punto de acceso
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BsForm.Group>
            <BsForm.Label className="fw-semibold">Nombre del clon</BsForm.Label>
            <BsForm.Control
              type="text"
              value={cloneName}
              onChange={e => setCloneName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCloneConfirm(); }}
              placeholder="Nombre del nuevo punto de acceso…"
              autoFocus
            />
            <BsForm.Text className="text-muted">
              Se copiará toda la configuración (marca, modelo, firmware, radio, PoE, montaje, ubicación,
              controlador y sus SSIDs). No se copiará el código, el número de serie, la MAC de gestión ni
              la IP de gestión.
            </BsForm.Text>
          </BsForm.Group>
          {cloneError && <div className="alert alert-danger mt-3 mb-0 py-2">{cloneError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={cloning}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCloneConfirm} disabled={cloning || !cloneName.trim()}>
            {cloning ? '⏳ Clonando…' : '📋 Clonar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── SmartBar (solo en la ficha clásica; en tarjetas va a badges/chips) ── */}
      {!isCards && (
      <div className="dv-smartbar">

        {/* Estándar */}
        <div className="dv-smart-btn" title={stdLabel}>
          <span className="dv-smart-btn-icon">
            <WifiIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stdLabel}
          </span>
          <span className="dv-smart-btn-label">Estándar</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Banda */}
        <div className="dv-smart-btn" title={bandLabel}>
          <span className="dv-smart-btn-icon">
            <SignalIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {bandLabel}
          </span>
          <span className="dv-smart-btn-label">Banda</span>
        </div>

        <div className="dv-smart-sep" />

        {/* SSIDs */}
        <button
          type="button"
          className="dv-smart-btn"
          onClick={() => setTab('radio')}
          title="Ver SSIDs"
        >
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>📡</span>
          </span>
          <span className="dv-smart-btn-value" style={{ color: ssidCount > 0 ? '#0369a1' : '#94a3b8' }}>
            {ssidCount}
          </span>
          <span className="dv-smart-btn-label">SSIDs</span>
        </button>

        <div className="dv-smart-sep" />

        {/* Controlador */}
        <button
          type="button"
          className="dv-smart-btn"
          onClick={() => setTab('ubicacion')}
          title={ctrlName || 'Standalone'}
        >
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>📡</span>
          </span>
          <span className="dv-smart-btn-value" style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ctrlName || 'Standalone'}
          </span>
          <span className="dv-smart-btn-label">Controlador</span>
        </button>

        <div className="dv-smart-sep" />

        {/* PoE */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <BoltIcon style={{ width: 18, height: 18, color: form.poe_powered ? '#f59e0b' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: form.poe_powered ? '#f59e0b' : '#94a3b8' }}>
            {form.poe_powered ? 'PoE' : '—'}
          </span>
          <span className="dv-smart-btn-label">Alimentación</span>
        </div>

        {isEditing && (
          <>
            <div className="dv-smart-sep" />
            <DocSmartButton elementType="wifi_ap" elementId={ap.id} elementName={form.name} companyId={companyId} />
          </>
        )}

      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={e => e.preventDefault()}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <>
                <div className="sf2-head">
                  <div className="sf2-head-icon" aria-hidden="true">
                    <WifiIcon style={{ width: 20, height: 20 }} />
                  </div>
                  <div className="sf2-head-title">
                    <div className="sf2-title-row">
                      <input
                        className="sf2-head-input"
                        value={form.name}
                        onChange={e => sf('name', e.target.value)}
                        placeholder="Nombre del punto de acceso…"
                        disabled={!editing}
                      />
                      {editing ? (
                        <>
                          <button
                            type="button"
                            className="sf2-title-btn sf2-title-btn--ok"
                            onClick={handleSave}
                            disabled={saving}
                            title="Guardar cambios"
                          >
                            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                          </button>
                          <button
                            type="button"
                            className="sf2-title-btn"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            title="Cancelar la edición y descartar cambios"
                          >
                            <XMarkIcon />
                          </button>
                        </>
                      ) : (
                        <button type="button" className="sf2-title-btn" onClick={() => setEditing(true)} title="Editar la ficha">
                          <PencilSquareIcon />
                        </button>
                      )}
                    </div>
                    <div className="sf2-crumb">
                      {[form.code, ctrlName || 'Standalone', rutaUbicacion].filter(Boolean).join(' › ')}
                    </div>
                  </div>
                  <div className="sf2-badges">
                    <span className="sf2-badge"><i style={{ '--c': estadoColor }} />{statusInfo.label}</span>
                    <span className="sf2-badge"><i style={{ '--c': form.poe_powered ? '#f59e0b' : '#94a3b8' }} />{form.poe_powered ? 'PoE' : 'Sin PoE'}</span>
                    <span className="sf2-badge">{ctrlName || 'Standalone'}</span>
                    {form.firmware_version && <span className="sf2-badge">FW {form.firmware_version}</span>}
                  </div>
                </div>
                <div className="sf2-chips">
                  <span className="sf2-chip sf2-chip--flat"><b>{stdLabel}</b><span>estándar</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{bandLabel}</b><span>banda</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{ssidCount}</b><span>SSIDs</span></span>
                  {accessCount > 0 && (
                    <button type="button" className="sf2-chip" onClick={() => setTab('access')}>
                      <b>{accessCount}</b><span>accesos</span>
                    </button>
                  )}
                  <span className="sf2-chip sf2-chip--flat"><b>{mountLabel}</b><span>montaje</span></span>
                  {ap?.id && (
                    <DocSmartButton elementType="wifi_ap" elementId={ap.id} elementName={form.name} companyId={companyId} variant="chip" />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Título */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="dv-record-title"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nombre del punto de acceso…"
                    style={{ flex: 1 }}
                  />
                  <button type="button" title="Sugerir nombre" onClick={handleSuggest} disabled={suggesting}
                    style={{ background: 'none', border: '1px solid rgba(255,255,255,.12)', borderRadius: 6, padding: 8, cursor: suggesting ? 'wait' : 'pointer', color: suggesting ? 'rgba(255,255,255,.35)' : '#a78bfa', flexShrink: 0 }}>
                    <SparklesIcon style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(t => (
              <TabButton
                key={t.key}
                type="button"
                className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && tab === 'resumen' && (
            <div className={paneClass}>
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>SSIDs</span><b>{ssidCount}</b><small>{isStandalone ? 'propios' : 'del controlador'}</small></div>
                <div className="sf2-kpi"><span>Máx. clientes</span><b>{form.max_clients || '—'}</b></div>
                <div className="sf2-kpi"><span>Potencia TX</span><b>{form.tx_power_dbm ? `${form.tx_power_dbm} dBm` : '—'}</b></div>
                <div className="sf2-kpi"><span>Accesos</span><b>{accessCount}</b></div>
                <div className="sf2-kpi"><span>Alimentación</span><b>{form.poe_powered ? 'PoE' : '—'}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código</label>
                      {editing ? (
                        <div className="sf2-input-row">
                          <input value={form.code} onChange={e => sf('code', e.target.value)} placeholder="WAP-0001" />
                          <button
                            type="button"
                            className="sf2-icon-btn"
                            title="Generar código con la nomenclatura"
                            aria-label="Generar código con la nomenclatura"
                            onClick={handleSuggestCode}
                            disabled={suggestingCode}
                          >
                            {suggestingCode ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 16, height: 16 }} />}
                          </button>
                        </div>
                      ) : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field"><label>Nombre</label><div className="v">{form.name || '—'}</div></div>
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing ? (
                        <select value={form.vendor} onChange={e => handleVendorChange(e.target.value)}>
                          {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                      ) : (
                        <div className="v">{VENDORS.find(v => v.value === form.vendor)?.label || form.vendor || '—'}</div>
                      )}
                    </div>
                    <CampoSelect
                      label="Modelo"
                      name="model"
                      value={form.model}
                      onChange={e => sf('model', e.target.value)}
                      editing={editing}
                      options={opcionesModelo}
                      onAdd={saveModelToCatalog}
                      addPlaceholder={marcaCatalogo ? `Nuevo modelo de ${marcaCatalogo}` : 'Nuevo modelo'}
                      addLabel="Añadir modelo al catálogo"
                      disabled={catalogSaving}
                    />
                    {catalogError && (
                      <div className="sf2-error" style={{ gridColumn: '1 / -1' }}>{catalogError}</div>
                    )}
                    <div className="sf2-field">
                      <label>Nº de serie</label>
                      {editing
                        ? <input value={form.serial_number} onChange={e => sf('serial_number', e.target.value)} />
                        : <div className="v">{form.serial_number || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Asset tag</label>
                      {editing
                        ? <input value={form.asset_tag} onChange={e => sf('asset_tag', e.target.value)} />
                        : <div className="v">{form.asset_tag || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Firmware</label>
                      {editing
                        ? <input value={form.firmware_version} onChange={e => sf('firmware_version', e.target.value)} placeholder="6.6.55" />
                        : <div className="v">{form.firmware_version || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Radio{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Estándar WiFi</label>
                      {editing ? (
                        <select value={form.wifi_standard} onChange={e => sf('wifi_standard', e.target.value)}>
                          {WIFI_STANDARDS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : <div className="v">{stdLabel}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Banda(s)</label>
                      {editing ? (
                        <select value={form.band} onChange={e => sf('band', e.target.value)}>
                          {BANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      ) : <div className="v">{bandLabel}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Potencia TX (dBm)</label>
                      {editing
                        ? <input type="number" min="0" max="40" value={form.tx_power_dbm} onChange={e => sf('tx_power_dbm', e.target.value)} placeholder="23" />
                        : <div className="v">{form.tx_power_dbm ? `${form.tx_power_dbm} dBm` : '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Máx. clientes</label>
                      {editing
                        ? <input type="number" min="0" value={form.max_clients} onChange={e => sf('max_clients', e.target.value)} placeholder="128" />
                        : <div className="v">{form.max_clients || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Alimentado por PoE</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" checked={!!form.poe_powered} onChange={e => sf('poe_powered', e.target.checked)} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.poe_powered ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => sf('status', e.target.value)}>
                          <option value="active">Activo</option>
                          <option value="maintenance">Mantenimiento</option>
                          <option value="planned">Planificado</option>
                          <option value="decom">Decom</option>
                        </select>
                      ) : <div className="v">{statusInfo.label}</div>}
                    </div>
                  </div>
                  <span
                    className="sf2-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => setTab('radio')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('radio'); } }}
                  >Ver SSIDs y radio →</span>
                </div>

                <div className="sf2-card">
                  <h3>Ubicación y montaje{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => sf('location_id', id)}
                          placeholder="Sin asignar"
                        />
                      ) : (
                        <div className="v">{rutaUbicacion || '—'}</div>
                      )}
                    </div>
                    <div className="sf2-field">
                      <label>Tipo de montaje</label>
                      {editing ? (
                        <select value={form.mount_type} onChange={e => sf('mount_type', e.target.value)}>
                          {MOUNT_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      ) : <div className="v">{mountLabel}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Planta / nivel</label>
                      {editing
                        ? <input value={form.location_floor} onChange={e => sf('location_floor', e.target.value)} placeholder="Planta 2, Sótano-1…" />
                        : <div className="v">{form.location_floor || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Sala / zona</label>
                      {editing
                        ? <input value={form.location_room} onChange={e => sf('location_room', e.target.value)} placeholder="Sala reuniones A…" />
                        : <div className="v">{form.location_room || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Red y controlador{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>MAC de gestión</label>
                      {editing
                        ? <input value={form.mac_address} onChange={e => sf('mac_address', e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
                        : <div className="v">{form.mac_address || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>IP de gestión</label>
                      {editing
                        ? <input value={form.management_ip} onChange={e => sf('management_ip', e.target.value)} placeholder="10.0.10.50" />
                        : <div className="v">{form.management_ip || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Controlador WiFi</label>
                      {editing ? (
                        <select value={form.controller_id} onChange={e => sf('controller_id', e.target.value)}>
                          <option value="">Sin controlador (standalone)</option>
                          {controllers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      ) : <div className="v">{ctrlName || 'Standalone'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>SSIDs</label>
                      <div className="v">{ssidCount} {isStandalone ? 'propios' : 'heredados del controlador'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {isEditing && (
                      <span
                        className="sf2-link"
                        role="button"
                        tabIndex={0}
                        onClick={() => setTab('ipam')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('ipam'); } }}
                      >Ver IPAM →</span>
                    )}
                    {isEditing && (
                      <span
                        className="sf2-link"
                        role="button"
                        tabIndex={0}
                        onClick={() => setTab('cable')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('cable'); } }}
                      >Ver cableado →</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: General ── */}
          {tab === 'general' && (
            <div className={paneClass}>

              <div className="dv-section">
                <div className="dv-section-title">Identificación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código</label>
                    <div className="d-flex gap-1">
                      <input className="dv-input" name="code" value={form.code} onChange={handleChange} placeholder="WAP-0001" style={{ minWidth: 0 }} />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        title="Generar código con la nomenclatura"
                        aria-label="Generar código con la nomenclatura"
                        onClick={handleSuggestCode}
                        disabled={suggestingCode}
                      >
                        {suggestingCode ? <span className="spinner-border spinner-border-sm" /> : <SparklesIcon style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Nombre <span className="text-danger">*</span></label>
                    <input className="dv-input" name="name" value={form.name} onChange={handleChange} placeholder="AP-PLANTA2-SALA-A" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fabricante</label>
                    <select className="dv-select" name="vendor" value={form.vendor} onChange={e => handleVendorChange(e.target.value)}>
                      {VENDORS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Modelo</label>
                    <div className="d-flex gap-1">
                      <select
                        className="dv-select"
                        style={{ flex: '1 1 auto', minWidth: 0 }}
                        value={form.model}
                        onChange={e => { sf('model', e.target.value); setAddingModel(false); setNewModel(''); }}
                      >
                        {opcionesModelo.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm text-nowrap"
                        title="Dar de alta un modelo en el maestro"
                        onClick={() => { setAddingModel(v => !v); setCatalogError(''); }}
                      >
                        + Modelo
                      </button>
                    </div>
                    {addingModel && (
                      <div className="d-flex gap-1 mt-2">
                        <input
                          className="dv-input"
                          style={{ minWidth: 0 }}
                          placeholder={marcaCatalogo ? `Nuevo modelo de ${marcaCatalogo}` : 'Nuevo modelo'}
                          value={newModel}
                          onChange={e => setNewModel(e.target.value)}
                          onKeyDown={e => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            const n = newModel.trim();
                            if (!n) return;
                            saveModelToCatalog(n);
                            setNewModel('');
                            setAddingModel(false);
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={catalogSaving || !newModel.trim()}
                          onClick={() => {
                            const n = newModel.trim();
                            if (!n) return;
                            saveModelToCatalog(n);
                            setNewModel('');
                            setAddingModel(false);
                          }}
                        >
                          Guardar
                        </button>
                      </div>
                    )}
                    {catalogError && <small className="text-danger d-block mt-1">{catalogError}</small>}
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Red</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Dirección MAC (gestión)</label>
                    <input className="dv-input" name="mac_address" value={form.mac_address} onChange={handleChange} placeholder="AA:BB:CC:DD:EE:FF" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">IP de gestión <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>(se sincroniza con IPAM)</span></label>
                    <input className="dv-input" name="management_ip" value={form.management_ip} onChange={handleChange} placeholder="10.0.10.50" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Controlador WiFi</label>
                    <select className="dv-select" name="controller_id" value={form.controller_id} onChange={handleChange}>
                      <option value="">Sin controlador (standalone)</option>
                      {controllers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Inventario</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input className="dv-input" name="serial_number" value={form.serial_number} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Asset tag</label>
                    <input className="dv-input" name="asset_tag" value={form.asset_tag} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Versión firmware</label>
                    <input className="dv-input" name="firmware_version" value={form.firmware_version} onChange={handleChange} placeholder="6.6.55" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        name="poe_powered"
                        checked={form.poe_powered}
                        onChange={handleChange}
                        style={{ marginRight: 6 }}
                      />
                      Alimentado por PoE
                    </label>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ── Tab: IPAM ── */}
          {tab === 'ipam' && isEditing && (
            <div className={paneClass}>
              <ElementIpPanel
                objectType="wifi_ap"
                objectId={ap?.id}
                companyId={companyId}
                elementName={ap?.name || form.name}
              />
            </div>
          )}

          {/* ── Tab: Conexión (cableado del AP) ── */}
          {tab === 'cable' && isEditing && (
            <div className={paneClass}>
              {/* Diagrama desde el punto de vista del AP: su uplink y la cadena
                  completa (toma → panel → switch), con el cable de cada tramo. */}
              {ap?.id && (
                <div className="dv-section">
                  <ConnectionDiagram
                    kind="ap"
                    id={ap.id}
                    companyId={companyId}
                    title={`Conexiones de ${ap?.name || form.name}`}
                  />
                </div>
              )}
              <div className="dv-section">
                <div className="dv-section-title">Cableado del punto de acceso</div>
                <p className="dv-hint" style={{ marginBottom: 12 }}>
                  El uplink del AP se conecta a una toma de pared (y de ahí al patch panel) o directamente a un
                  puerto de switch. También puedes tenderlo desde el puerto del switch: el AP aparecerá como destino.
                </p>
                <WifiApPortsPanel ap={ap} companyId={companyId} />
              </div>
            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {tab === 'access' && isEditing && (
            <div className={paneClass}>
              <DeviceAccessPanel
                objectType="wifi_access_point"
                objectId={ap?.id}
                companyId={companyId}
                elementName={ap?.name || form.name}
                defaultHost={form.management_ip}
                onCountChange={setAccessCount}
                canEdit={editing}
              />
            </div>
          )}

          {/* ── Tab: Radio / SSIDs ── */}
          {tab === 'radio' && (
            <div className={paneClass}>

              <div className="dv-section">
                <div className="dv-section-title">Especificaciones radio</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Estándar WiFi</label>
                    <select className="dv-select" name="wifi_standard" value={form.wifi_standard} onChange={handleChange}>
                      {WIFI_STANDARDS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Banda(s)</label>
                    <select className="dv-select" name="band" value={form.band} onChange={handleChange}>
                      {BANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Potencia TX (dBm)</label>
                    <input className="dv-input" name="tx_power_dbm" type="number" min="0" max="40" value={form.tx_power_dbm} onChange={handleChange} placeholder="23" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Máx. clientes</label>
                    <input className="dv-input" name="max_clients" type="number" min="0" value={form.max_clients} onChange={handleChange} placeholder="128" />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">SSIDs</div>

                {/* APs con controlador: SSIDs heredados (read-only) */}
                {!isStandalone && (
                  <>
                    <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.9rem', background: 'rgba(59,130,246,.10)', border: '1px solid rgba(59,130,246,.35)', borderRadius: 6, fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
                      📡 Los SSIDs son gestionados por el controlador <strong>{ctrlName}</strong>. Los APs los emiten automáticamente según la configuración del controlador.
                    </div>
                    {ctrlSsids.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>El controlador no tiene SSIDs definidos todavía.</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ctrlSsids.map(s => (
                          <div key={s.id} style={{ background: 'rgba(var(--accent-rgb), .08)', border: '1px solid rgba(var(--accent-rgb), .3)', borderRadius: 6, padding: '4px 10px', fontSize: '0.82rem' }}>
                            <strong style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{s.name}</strong>
                            <span style={{ color: 'var(--accent)', marginLeft: 8 }}>{s.security_mode_label || s.security_mode}</span>
                            {s.vlan_id && <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>VLAN {s.vlan_id}</span>}
                            {(s.security_mode === 'wpa2_enterprise' || s.security_mode === 'wpa3_enterprise') && s.radius_server && (
                              <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '0.78rem' }}>RADIUS: {s.radius_server}</span>
                            )}
                            {s.hidden && <span style={{ color: '#dc2626', marginLeft: 8 }}>🔴 Oculta</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* AP standalone: SSIDs propios con CRUD */}
                {isStandalone && (
                  <>
                    {apSsids.length > 0 && (
                      <div className="wol-table-wrap" style={{ marginBottom: '1rem' }}>
                        <table className="wol-table">
                          <thead>
                            <tr><th>Nombre (SSID)</th><th>Seguridad</th><th>Banda</th><th>VLAN</th><th>Estado</th><th></th></tr>
                          </thead>
                          <tbody>
                            {apSsids.map(s => {
                              const sc = AP_SECURITY_COLORS[s.security_mode] || {};
                              return (
                              <tr key={s.id}>
                                <td><strong style={{ fontFamily: 'monospace' }}>{s.name}</strong></td>
                                <td>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: sc.bg || 'rgba(148,163,184,.16)', color: sc.color || 'var(--text-secondary)' }}>
                                    {s.security_mode_label || s.security_mode}
                                  </span>
                                </td>
                                <td style={{ fontSize: '0.8rem' }}>{s.band_filter_label || s.band_filter}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.vlan_id || '—'}</td>
                                <td style={{ fontSize: '0.8rem', color: s.status === 'active' ? '#16a34a' : '#dc2626' }}>● {s.status === 'active' ? 'Activo' : 'Off'}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '2px 6px', fontSize: '0.72rem', marginRight: 3 }}
                                    onClick={() => setEditSsid({ ...s, vlan_id: s.vlan_id ?? '' })}>✏️</button>
                                  <button type="button" className="dv-btn dv-btn-danger" style={{ padding: '2px 6px', fontSize: '0.72rem' }}
                                    onClick={() => handleSsidDelete(s.id)}>🗑️</button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {apSsids.length === 0 && !editSsid && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Ninguna red WiFi (SSID) configurada en este AP.</p>
                    )}
                    {!isEditing && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Guarda el AP primero para poder añadir SSIDs propios.</p>
                    )}
                    {isEditing && !editSsid && (
                      <button type="button" className="dv-btn dv-btn-primary" style={{ marginBottom: '0.5rem' }}
                        onClick={() => setEditSsid({ ...AP_EMPTY_SSID })}>
                        + Añadir SSID
                      </button>
                    )}
                    {editSsid && (
                      <ApSsidInlineForm
                        ssid={editSsid}
                        saving={ssidSaving}
                        error={ssidError}
                        radiusServers={radiusServers}
                        onSave={handleSsidSave}
                        onCancel={() => { setEditSsid(null); setSsidError(''); }}
                      />
                    )}
                  </>
                )}
              </div>

            </div>
          )}

          {/* ── Tab: Ubicación ── */}
          {tab === 'ubicacion' && (
            <div className={paneClass}>

              <div className="dv-section">
                <div className="dv-section-title">Localización</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Sede / Location</label>
                    <LocationTreeSelect
                      locations={locations}
                      value={form.location_id}
                      onChange={(id) => setForm(f => ({ ...f, location_id: id }))}
                      placeholder="Sin asignar"
                      disabled={!editing}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Planta / Nivel</label>
                    <input className="dv-input" name="location_floor" value={form.location_floor} onChange={handleChange} placeholder="Planta 2, Sótano-1, PB…" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sala / Zona</label>
                    <input className="dv-input" name="location_room" value={form.location_room} onChange={handleChange} placeholder="Sala reuniones A, Pasillo norte…" />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Montaje</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Tipo de montaje</label>
                    <select className="dv-select" name="mount_type" value={form.mount_type} onChange={handleChange}>
                      {MOUNT_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

            </div>
          )}

          <FormDirtyDock
            show={isCards && editing && isDirty}
            saving={saving}
            onDiscard={handleCancelEdit}
            onSave={handleSave}
          />

          </fieldset>
          </FormCardsShell>

        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="wifi_ap"
          ciId={ap?.id}
          tipoLegible="el punto de acceso"
        />
      </div>
    </div>
  );
}

// ── AP_EMPTY_SSID + ApSsidInlineForm ───────────────────────────────────────────────────────
const AP_EMPTY_SSID = {
  name: '', security_mode: 'wpa2_psk',
  radius_server_id: null, radius_server: '', radius_port: 1812,
  vlan_id: '', band_filter: 'all',
  hidden: false, client_isolation: false,
  status: 'active', notes: '',
};

const AP_SECURITY_OPTIONS = [
  { value: 'open',            label: 'Abierta (sin contraseña)',   isEnterprise: false },
  { value: 'wpa2_psk',        label: 'WPA2-Personal (PSK)',        isEnterprise: false },
  { value: 'wpa2_enterprise', label: 'WPA2-Enterprise (RADIUS)',   isEnterprise: true  },
  { value: 'wpa3_psk',        label: 'WPA3-Personal (SAE)',        isEnterprise: false },
  { value: 'wpa3_enterprise', label: 'WPA3-Enterprise (RADIUS)',   isEnterprise: true  },
  { value: 'wpa3_transition', label: 'WPA2/WPA3 Transition Mode',  isEnterprise: false },
  { value: 'owe',             label: 'OWE (Enhanced Open)',        isEnterprise: false },
];

const AP_BAND_FILTER_OPTIONS = [
  { value: 'all',      label: 'Todas las bandas' },
  { value: '2.4_only', label: 'Solo 2.4 GHz' },
  { value: '5_only',   label: 'Solo 5 GHz' },
  { value: '6_only',   label: 'Solo 6 GHz' },
  { value: 'no_24',    label: 'Sin 2.4 GHz (5 + 6 GHz)' },
];

// Chips de seguridad de los SSID: tono medio saturado + fondo translúcido,
// legible tanto en tema oscuro como claro (mismos que el formulario del controlador).
const AP_SECURITY_COLORS = {
  open:            { bg: 'rgba(220,38,38,.16)',  color: '#dc2626' },
  wpa2_psk:        { bg: 'rgba(217,119,6,.16)',  color: '#d97706' },
  wpa2_enterprise: { bg: 'rgba(37,99,235,.16)',  color: '#2563eb' },
  wpa3_psk:        { bg: 'rgba(22,163,74,.16)',  color: '#16a34a' },
  wpa3_enterprise: { bg: 'rgba(124,58,237,.16)', color: '#7c3aed' },
  wpa3_transition: { bg: 'rgba(37,99,235,.13)',  color: '#2563eb' },
  owe:             { bg: 'rgba(5,150,105,.14)',  color: '#059669' },
};

function ApSsidInlineForm({ ssid, saving, error, onSave, onCancel, radiusServers = [] }) {
  const [f, setF] = useState({ ...ssid });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };
  const isEnterprise = f.security_mode === 'wpa2_enterprise' || f.security_mode === 'wpa3_enterprise';
  const selectedRadius = radiusServers.find(r => String(r.id) === String(f.radius_server_id));

  return (
    <div style={{ background: 'rgba(var(--accent-rgb), .07)', border: '1px solid rgba(var(--accent-rgb), .35)', borderRadius: 8, padding: '1rem 1.25rem', marginTop: '0.5rem' }}>
      <div style={{ fontWeight: 600, color: 'var(--accent)', marginBottom: '0.75rem', fontSize: '0.88rem' }}>
        {f.id ? `✏️ Editar SSID: "${f.name}"` : '+ Nuevo SSID'}
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.5rem' }}>⚠ {error}</div>}
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Nombre (SSID) <span className="text-danger">*</span></label>
          <input className="dv-input" name="name" value={f.name} onChange={handleChange} placeholder="CorpNet, Guest-WiFi…" maxLength={32} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{f.name.length}/32 caracteres</div>
        </div>
        <div className="col-md-4">
          <label className="form-label">Modo de seguridad</label>
          <select className="dv-select" name="security_mode" value={f.security_mode} onChange={handleChange}>
            {AP_SECURITY_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">VLAN</label>
          <input className="dv-input" name="vlan_id" type="number" min="1" max="4094" value={f.vlan_id} onChange={handleChange} placeholder="10" />
        </div>
        <div className="col-md-2">
          <label className="form-label">Banda</label>
          <select className="dv-select" name="band_filter" value={f.band_filter} onChange={handleChange}>
            {AP_BAND_FILTER_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
        {isEnterprise && (
          <div className="col-12">
            <label className="form-label">Servidor RADIUS</label>
            {radiusServers.length > 0 && (
              <select
                className="dv-select"
                style={{ marginBottom: 6 }}
                value={f.radius_server_id || ''}
                onChange={e => {
                  const id = e.target.value || null;
                  const rs = radiusServers.find(r => String(r.id) === String(id));
                  setF(prev => ({
                    ...prev,
                    radius_server_id: id ? Number(id) : null,
                    radius_server: rs ? rs.host : prev.radius_server,
                    radius_port: rs ? rs.auth_port : prev.radius_port,
                  }));
                }}
              >
                <option value="">— Manual (introducir host) —</option>
                {radiusServers.map(r => (
                  <option key={r.id} value={r.id}>{r.name} ({r.host}:{r.auth_port})</option>
                ))}
              </select>
            )}
            {selectedRadius ? (
              <div style={{ padding: '4px 10px', background: 'rgba(var(--accent-rgb), .14)', border: '1px solid rgba(var(--accent-rgb), .35)', borderRadius: 6, fontSize: '0.78rem', color: 'var(--accent)' }}>
                🔐 {selectedRadius.host} · Auth:{selectedRadius.auth_port} · Acct:{selectedRadius.acct_port}
              </div>
            ) : (
              <div className="row g-2">
                <div className="col-md-8">
                  <input className="dv-input" name="radius_server" value={f.radius_server} onChange={handleChange} placeholder="10.0.1.5" />
                </div>
                <div className="col-md-4">
                  <input className="dv-input" name="radius_port" type="number" value={f.radius_port} onChange={handleChange} placeholder="1812" />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="col-md-2">
          <label className="form-label">Estado</label>
          <select className="dv-select" name="status" value={f.status} onChange={handleChange}>
            <option value="active">Activo</option>
            <option value="disabled">Desactivado</option>
          </select>
        </div>
        <div className="col-12">
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 4 }}>
            <input type="checkbox" name="hidden" checked={f.hidden} onChange={handleChange} />
            Red oculta (no broadcast SSID)
          </label>
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" name="client_isolation" checked={f.client_isolation} onChange={handleChange} />
            Aislamiento de clientes
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button type="button" className="dv-btn dv-btn-primary" onClick={() => onSave(f)} disabled={saving}>
          {saving ? 'Guardando…' : (f.id ? 'Actualizar SSID' : 'Crear SSID')}
        </button>
        <button type="button" className="dv-btn dv-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
      </div>
    </div>
  );
}
